import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  MessageSquareText, Users, Clock, Pencil, X, Plus, Download, Wand2,
  Sparkles, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_PREFIX = 'consistency_creator_intel_';
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_INTEL = {
  message: { core_message: '', content_themes: [], target_outcome: '' },
  audience: { primary_audience: '', audience_interests: [], demographics: '', geography: '' },
  postingTimes: { days: [], time_window: '', timezone: '', platform_notes: '' },
};

const CARD_META = {
  message: { title: 'Creator Message', icon: MessageSquareText, color: 'text-blue-400' },
  audience: { title: 'Target Audience', icon: Users, color: 'text-purple-400' },
  postingTimes: { title: 'Preferred Posting Times', icon: Clock, color: 'text-emerald-400' },
};

// Static "Analytics Recommendation" examples — clearly labeled demo insight,
// not derived from any real performance data in this environment yet.
const RECOMMENDATIONS = {
  message: { target_outcome: 'Lead with a concrete outcome creators can picture — framing like this tends to hold attention longer for how-to content.' },
  audience: { primary_audience: 'Aspiring creators, 18–34, interested in practical growth tips' },
  postingTimes: { days: ['Tue', 'Thu'], time_window: '7:00 PM – 9:00 PM', platform_notes: 'Evenings tend to outperform mornings for this content type' },
};

const STARTER_SUGGESTIONS = {
  message: { core_message: 'Helping everyday creators build a sustainable content business', content_themes: ['Tutorials', 'Behind-the-scenes', 'Tips & Tricks'], target_outcome: 'Grow a loyal, engaged audience' },
  audience: { primary_audience: 'Aspiring content creators', audience_interests: ['Content strategy', 'Growth tips', 'Editing tools'], demographics: '18–34, roughly even split', geography: 'US, UK, Canada, Australia' },
  postingTimes: { days: ['Tue', 'Thu', 'Sat'], time_window: '7:00 PM – 9:00 PM', timezone: 'America/New_York', platform_notes: 'YouTube: evenings · TikTok: midday & evening' },
};

function loadIntel(creatorId) {
  try { return { ...DEFAULT_INTEL, ...(JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}${creatorId}`)) || {}) }; }
  catch { return DEFAULT_INTEL; }
}
function saveIntel(creatorId, intel) {
  localStorage.setItem(`${STORAGE_PREFIX}${creatorId}`, JSON.stringify(intel));
}

function TagInput({ label, values, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const add = () => { if (draft.trim()) { onChange([...values, draft.trim()]); setDraft(''); } };
  return (
    <div>
      <Label className="text-[#8A93A6] text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1 mb-1.5">
        {values.map((v, i) => (
          <Badge key={`${v}-${i}`} className="bg-white/5 text-[#C0C7D4] border-white/10 text-[11px] gap-1">
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} data-testid={`tag-remove-${label}-${i}`}><X className="h-2.5 w-2.5" /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} placeholder={placeholder} className="bg-white/5 border-white/10 text-white h-8 text-sm" />
        <Button type="button" size="sm" variant="outline" className="h-8 border-white/10 shrink-0" onClick={add}><Plus className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

function EditDialog({ cardType, draft, setDraft, onClose, onSave }) {
  const meta = CARD_META[cardType];
  const rec = RECOMMENDATIONS[cardType];
  const applyRecommendation = () => { setDraft(d => ({ ...d, ...rec })); toast.success('Recommendation copied into the fields below — review and Save to keep it.'); };
  const startFresh = () => { setDraft(STARTER_SUGGESTIONS[cardType]); toast.info('Guided starter suggestions filled in (example only) — edit anything before saving.'); };
  const importFromPlatforms = () => { setDraft(STARTER_SUGGESTIONS[cardType]); toast.info('Simulated import — no real connected-platform data available yet, these are placeholder values to edit.'); };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`intel-edit-dialog-${cardType}`}>
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><meta.icon className={`h-4.5 w-4.5 ${meta.color}`} />{meta.title}</DialogTitle></DialogHeader>

        <div className="flex flex-wrap gap-1.5">
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={importFromPlatforms} data-testid={`intel-import-${cardType}`}><Download className="h-3 w-3 mr-1" />Import from Connected Platforms</Button>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={startFresh} data-testid={`intel-startfresh-${cardType}`}><Wand2 className="h-3 w-3 mr-1" />Start Fresh</Button>
        </div>

        <div className="rounded-lg border border-blue-500/25 bg-blue-500/[0.06] p-3 space-y-2">
          <p className="text-xs text-blue-300 font-medium flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Analytics Recommendation (demo insight)</p>
          <p className="text-xs text-[#C0C7D4]">{Object.values(rec)[0] instanceof Array ? Object.values(rec)[0].join(', ') : Object.values(rec)[0]}</p>
          <Button type="button" size="sm" className="h-7 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-300" onClick={applyRecommendation} data-testid={`intel-apply-rec-${cardType}`}><Check className="h-3 w-3 mr-1" />Apply Recommendation</Button>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-[#8A93A6] uppercase tracking-wider">Creator Preference — your own values, editable below</p>
          {cardType === 'message' && (
            <>
              <div><Label className="text-[#8A93A6] text-xs">Core message or purpose</Label><Textarea value={draft.core_message} onChange={e => setDraft(d => ({ ...d, core_message: e.target.value }))} className="bg-white/5 border-white/10 text-white resize-none mt-1" rows={2} data-testid="intel-message-core" /></div>
              <TagInput label="Content themes" values={draft.content_themes} onChange={v => setDraft(d => ({ ...d, content_themes: v }))} placeholder="Add a theme..." />
              <div><Label className="text-[#8A93A6] text-xs">Target outcome</Label><Input value={draft.target_outcome} onChange={e => setDraft(d => ({ ...d, target_outcome: e.target.value }))} className="bg-white/5 border-white/10 text-white mt-1" data-testid="intel-message-outcome" /></div>
            </>
          )}
          {cardType === 'audience' && (
            <>
              <div><Label className="text-[#8A93A6] text-xs">Primary audience</Label><Input value={draft.primary_audience} onChange={e => setDraft(d => ({ ...d, primary_audience: e.target.value }))} className="bg-white/5 border-white/10 text-white mt-1" data-testid="intel-audience-primary" /></div>
              <TagInput label="Audience interests" values={draft.audience_interests} onChange={v => setDraft(d => ({ ...d, audience_interests: v }))} placeholder="Add an interest..." />
              <div><Label className="text-[#8A93A6] text-xs">Demographics</Label><Input value={draft.demographics} onChange={e => setDraft(d => ({ ...d, demographics: e.target.value }))} placeholder="e.g. 18–34, mostly male" className="bg-white/5 border-white/10 text-white mt-1" data-testid="intel-audience-demo" /></div>
              <div><Label className="text-[#8A93A6] text-xs">Geography</Label><Input value={draft.geography} onChange={e => setDraft(d => ({ ...d, geography: e.target.value }))} placeholder="e.g. US, UK, Canada" className="bg-white/5 border-white/10 text-white mt-1" data-testid="intel-audience-geo" /></div>
            </>
          )}
          {cardType === 'postingTimes' && (
            <>
              <div>
                <Label className="text-[#8A93A6] text-xs">Preferred publishing days</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {DAYS.map(d => (
                    <button key={d} type="button" onClick={() => setDraft(prev => ({ ...prev, days: prev.days.includes(d) ? prev.days.filter(x => x !== d) : [...prev.days, d] }))} className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${draft.days.includes(d) ? 'bg-[#3A7BFF]/20 border-[#3A7BFF] text-white' : 'border-white/10 text-[#8A93A6] hover:text-white'}`} data-testid={`intel-day-${d}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div><Label className="text-[#8A93A6] text-xs">Preferred publishing times</Label><Input value={draft.time_window} onChange={e => setDraft(d => ({ ...d, time_window: e.target.value }))} placeholder="e.g. 7:00 PM – 9:00 PM" className="bg-white/5 border-white/10 text-white mt-1" data-testid="intel-time-window" /></div>
              <div><Label className="text-[#8A93A6] text-xs">Time zone</Label><Input value={draft.timezone} onChange={e => setDraft(d => ({ ...d, timezone: e.target.value }))} placeholder="e.g. America/New_York" className="bg-white/5 border-white/10 text-white mt-1" data-testid="intel-timezone" /></div>
              <div><Label className="text-[#8A93A6] text-xs">Platform-specific preferences</Label><Textarea value={draft.platform_notes} onChange={e => setDraft(d => ({ ...d, platform_notes: e.target.value }))} className="bg-white/5 border-white/10 text-white resize-none mt-1" rows={2} placeholder="e.g. YouTube: evenings · TikTok: midday" data-testid="intel-platform-notes" /></div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="border-white/10" onClick={onClose} data-testid={`intel-cancel-${cardType}`}>Cancel</Button>
          <Button className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={onSave} data-testid={`intel-save-${cardType}`}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Creator Intelligence Overview — three editable cards (Creator Message,
 * Target Audience, Preferred Posting Times) stored locally per creator.
 * This local shape is intentionally the same shape future onboarding would
 * populate — onboarding itself is not built here. */
export default function CreatorIntelligenceSection({ creatorId, prefill, onPrefillConsumed }) {
  const [intel, setIntel] = useState(DEFAULT_INTEL);
  const [editingCard, setEditingCard] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => { if (creatorId) setIntel(loadIntel(creatorId)); }, [creatorId]);

  useEffect(() => {
    if (!prefill) return;
    setEditingCard(prefill.cardType);
    setDraft({ ...intel[prefill.cardType], ...prefill.patch });
    onPrefillConsumed?.();
  }, [prefill]);

  const openEdit = (cardType) => { setEditingCard(cardType); setDraft(intel[cardType]); };
  const closeEdit = () => { setEditingCard(null); setDraft(null); };
  const handleSave = () => {
    const next = { ...intel, [editingCard]: draft };
    setIntel(next);
    saveIntel(creatorId, next);
    toast.success('Saved locally (demo) — this will power onboarding later, not sent anywhere yet.');
    closeEdit();
  };

  const summaryFor = (cardType) => {
    const v = intel[cardType];
    if (cardType === 'message') return v.core_message || (v.content_themes.length ? v.content_themes.join(', ') : 'Not set yet');
    if (cardType === 'audience') return v.primary_audience || (v.audience_interests.length ? v.audience_interests.join(', ') : 'Not set yet');
    return v.days.length ? `${v.days.join(', ')}${v.time_window ? ` · ${v.time_window}` : ''}` : 'Not set yet';
  };

  return (
    <div data-testid="creator-intelligence-section">
      <h2 className="text-lg font-semibold text-white mb-1">Creator Intelligence Overview</h2>
      <p className="text-xs text-[#8A93A6] mb-4">Your message, audience, and posting preferences — kept here so future features (like onboarding) can reuse them.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.keys(CARD_META).map((cardType) => {
          const meta = CARD_META[cardType];
          return (
            <Card key={cardType} className="glass border-white/5" data-testid={`intel-card-${cardType}`}>
              <CardContent className="pt-5 pb-5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-white"><meta.icon className={`h-4 w-4 ${meta.color}`} />{meta.title}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8A93A6]" onClick={() => openEdit(cardType)} data-testid={`intel-edit-btn-${cardType}`}><Pencil className="h-3.5 w-3.5" /></Button>
                </div>
                <p className="text-xs text-[#C0C7D4] line-clamp-3">{summaryFor(cardType)}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {editingCard && draft && (
        <EditDialog cardType={editingCard} draft={draft} setDraft={setDraft} onClose={closeEdit} onSave={handleSave} />
      )}
    </div>
  );
}
